/**
 * WebSocket Hook for Real-Time Communication
 * Handles notifications and chat messages in real-time
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 30000;

export const useWebSocket = (onNotification, onChatMessage, onTyping, onOnlineStatus) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const connectFnRef = useRef(null);

  // Store callbacks in refs to avoid stale closures
  const callbacksRef = useRef({
    onNotification,
    onChatMessage,
    onTyping,
    onOnlineStatus
  });

  useEffect(() => {
    callbacksRef.current = {
      onNotification,
      onChatMessage,
      onTyping,
      onOnlineStatus
    };
  }, [onNotification, onChatMessage, onTyping, onOnlineStatus]);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('WebSocket: No token available');
      return;
    }

    // Get WebSocket URL from backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // Handle empty or invalid backend URL
    if (!backendUrl) {
      console.log('WebSocket: No backend URL configured');
      setConnectionError('WebSocket not configured');
      return;
    }
    
    // Construct WebSocket URL correctly
    // Backend URL: https://stripe-sync-fix-6.preview.emergentagent.com
    // WebSocket URL should be: wss://clinic-memberships.preview.emergentagent.com/api/ws
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    // Remove trailing slash and /api suffix if present, then add /api/ws
    const baseUrl = backendUrl.replace(/\/$/, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}://${baseUrl.replace(/^https?:\/\//, '')}/api/ws?token=${token}`;

    console.log('WebSocket: Connecting to', wsUrl.replace(token, 'TOKEN'));

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        console.log('WebSocket: Connected');
        setIsConnected(true);
        setConnectionError(null);

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          const callbacks = callbacksRef.current;
          
          switch (data.type) {
            case 'notification':
              if (callbacks.onNotification) callbacks.onNotification(data.notification);
              break;
            case 'chat_message':
              if (callbacks.onChatMessage) callbacks.onChatMessage(data);
              break;
            case 'typing':
              if (callbacks.onTyping) callbacks.onTyping(data);
              break;
            case 'online_status':
              if (callbacks.onOnlineStatus) callbacks.onOnlineStatus(data);
              break;
            case 'pong':
              // Keep-alive response, ignore
              break;
            default:
              console.log('WebSocket: Unknown message type', data.type);
          }
        } catch (err) {
          console.error('WebSocket: Failed to parse message', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket: Error', error);
        setConnectionError('Connection error');
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        console.log('WebSocket: Disconnected', event.code, event.reason);
        setIsConnected(false);

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        // Attempt to reconnect if not intentionally closed
        if (event.code !== 1000 && event.code !== 4001) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && connectFnRef.current) {
              console.log('WebSocket: Reconnecting...');
              connectFnRef.current();
            }
          }, WS_RECONNECT_DELAY);
        }
      };
    } catch (err) {
      console.error('WebSocket: Connection failed', err);
      setConnectionError('Failed to connect');
    }
  }, []);

  // Store connect function in ref for reconnection
  useEffect(() => {
    connectFnRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  // Send chat message
  const sendChatMessage = useCallback((conversationId, message, attachments = [], isUrgent = false) => {
    return sendMessage({
      type: 'chat_message',
      conversation_id: conversationId,
      message,
      attachments,
      is_urgent: isUrgent
    });
  }, [sendMessage]);

  // Send typing indicator
  const sendTypingIndicator = useCallback((conversationId, isTyping) => {
    return sendMessage({
      type: 'typing',
      conversation_id: conversationId,
      is_typing: isTyping
    });
  }, [sendMessage]);

  // Mark messages as read
  const markAsRead = useCallback((conversationId) => {
    return sendMessage({
      type: 'mark_read',
      conversation_id: conversationId
    });
  }, [sendMessage]);

  useEffect(() => {
    mountedRef.current = true;
    
    // Use timeout to avoid calling setState synchronously in effect
    const connectTimeout = setTimeout(() => {
      connect();
    }, 0);

    return () => {
      mountedRef.current = false;
      clearTimeout(connectTimeout);
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionError,
    sendMessage,
    sendChatMessage,
    sendTypingIndicator,
    markAsRead,
    reconnect: connect,
    disconnect
  };
};

export default useWebSocket;
